import { useRef, useState, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import FullCalendar from "@fullcalendar/react";
import BootstrapTheme from "@fullcalendar/bootstrap";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import listPlugin from "@fullcalendar/list";
import multiMonthPlugin from "@fullcalendar/multimonth";
import interactionPlugin from "@fullcalendar/interaction";
import { Spinner } from "reactstrap";
import {
  DatesSetArg,
  EventClickArg,
  EventContentArg,
} from "@fullcalendar/core";
import { getWorkAssignments } from "features/WorkAssignments/services/WorkAssignmentService";
import type { WorkAssignment } from "features/WorkAssignments/types/work-assignment.types";
import { getHolidays } from "features/Holidays/services/HolidayService";
import {
  WorkAssignmentSourceType,
  WorkAssignmentSourceTypeLabel,
  WorkAssignmentStatus,
  workAssignmentStatusMap,
} from "features/WorkAssignments/constants/work-assignment.constants";
import {
  SOURCE_TYPE_URL_MAP,
  type AnySourceType,
} from "constants/source-type.constants";
import { useProfile } from "features/Authentication/hooks/useProfile";
import { RoleCode } from "features/RolesAndPermissions/constants/role.constants";
import { workOrderStatusMap } from "features/WorkOrders/constants/work-order.constants";
import { pettyWorkStatusMap } from "features/PettyWorks/constants/petty-work.constants";
import { amcStatusMap } from "features/Amc/constants/amc-contract.constants";
import { amcVisitStatusMap } from "features/AmcVisitLogs/constants/amc-visit.constants";
import { EnquirySiteVisitStatusMap } from "features/EnquirySiteVisits/constants/enquiry-site-visit.constants";
import { enquiryStatusMap } from "features/Enquiries/constants/enquiry.constants";
import WorkAssignmentListFilters from "./WorkAssignmentListFilters";


function resolveEventColor(a: WorkAssignment): string {
  return workAssignmentStatusMap[a.status as WorkAssignmentStatus]?.color ?? "secondary";
}

function assigneeName(a: WorkAssignment): string {
  const u = a.user;
  if (!u) return `User #${a.toUserId}`;
  return (
    [u.firstName, u.lastName].filter(Boolean).join(" ") ||
    u.email ||
    `User #${a.toUserId}`
  );
}

function sourceShortLabel(a: WorkAssignment): string {
  const code = (a.source as any)?.code?.trim();
  if (code) return code;
  const typeLabel = WorkAssignmentSourceTypeLabel[a.sourceType] ?? a.sourceType;
  return `${typeLabel} #${a.sourceId}`;
}

function sourceHref(a: WorkAssignment): string | null {
  const base = SOURCE_TYPE_URL_MAP[a.sourceType as AnySourceType] ?? null;
  if (!base) return null;
  return `${base}/${a.sourceId}`;
}

/** The source's own current status (e.g. Work Order "In progress") — distinct from the assignment's own status. */
function sourceStatusInfo(
  a: WorkAssignment,
): { label: string; color: string } | null {
  const status = (a.source as any)?.status;
  if (!status) return null;
  const mapByType: Partial<
    Record<WorkAssignmentSourceType, Record<string, { label: string; color: string }>>
  > = {
    [WorkAssignmentSourceType.WORK_ORDER]: workOrderStatusMap,
    [WorkAssignmentSourceType.PETTY_WORK]: pettyWorkStatusMap,
    [WorkAssignmentSourceType.AMC]: amcStatusMap,
    [WorkAssignmentSourceType.AMC_VISIT]: amcVisitStatusMap,
    [WorkAssignmentSourceType.SITE_VISIT]: EnquirySiteVisitStatusMap,
    [WorkAssignmentSourceType.ENQUIRY]: enquiryStatusMap,
  };
  return mapByType[a.sourceType]?.[status] ?? null;
}

function toYmd(d: Date): string {
  return [
    d.getFullYear(),
    String(d.getMonth() + 1).padStart(2, "0"),
    String(d.getDate()).padStart(2, "0"),
  ].join("-");
}

function exclusiveEnd(dateStr: string): string {
  const d = new Date(`${dateStr.slice(0, 10)}T12:00:00`);
  d.setDate(d.getDate() + 1);
  return d.toISOString().split("T")[0];
}

function EventContent({ info }: { info: EventContentArg }) {
  const a: WorkAssignment = info.event.extendedProps.assignment;
  const color: string = info.event.extendedProps.color ?? "secondary";
  const name = assigneeName(a);
  const src = sourceShortLabel(a);
  const typeLabel = WorkAssignmentSourceTypeLabel[a.sourceType] ?? a.sourceType;
  const statusInfo = sourceStatusInfo(a);
  return (
    <div
      className={`bg-${color}-subtle text-${color} w-100 overflow-hidden`}
      style={{
        borderRadius: 4,
        padding: "2px 6px",
        fontSize: "0.8rem",
        lineHeight: 1.35,
      }}
    >
      <div className="text-truncate">
        <span className="fw-semibold">{name}</span>
      </div>
      <div className="text-truncate" style={{ opacity: 0.8 }}>
        {typeLabel} · <span className="font-monospace">{src}</span>
      </div>
      {statusInfo ? (
        <div className="text-truncate">
          <span
            className={`badge bg-${statusInfo.color}-subtle text-${statusInfo.color}`}
            style={{ fontSize: "0.6rem", padding: "1px 4px", fontWeight: 500 }}
          >
            {statusInfo.label}
          </span>
        </div>
      ) : null}
    </div>
  );
}

const ScheduleCalendarView = () => {
  const navigate = useNavigate();
  const { user, highestRole } = useProfile();
  const isProjectEngineer = highestRole?.code === RoleCode.PROJECT_ENGINEER;
  const calendarRef = useRef<FullCalendar>(null);
  const [calLoading, setCalLoading] = useState(false);
  const [events, setEvents] = useState<any[]>([]);
  const [holidayMap, setHolidayMap] = useState<Map<string, string>>(new Map());
  const cellEls = useRef<Map<string, HTMLElement>>(new Map());

  const [selectedStatus, setSelectedStatus] = useState<
    WorkAssignmentStatus | ""
  >(WorkAssignmentStatus.ASSIGNED);
  const [selectedSourceType, setSelectedSourceType] = useState<
    WorkAssignmentSourceType | ""
  >("");
  const [assigneeUserId, setAssigneeUserId] = useState<number | null>(null);

  const currentRange = useRef<{ from: string; to: string } | null>(null);

  useEffect(() => {
    cellEls.current.forEach((el, dateStr) => {
      const name = holidayMap.get(dateStr);
      if (name) {
        el.title = name;
        el.style.cursor = "help";
      } else {
        el.removeAttribute("title");
        el.style.cursor = "";
      }
    });
  }, [holidayMap]);

  const doFetch = useCallback(
    async (from: string, to: string) => {
      setCalLoading(true);
      try {
        const startYear = parseInt(from.slice(0, 4), 10);
        const endYear = parseInt(to.slice(0, 4), 10);
        const holidayYears =
          startYear === endYear ? [startYear] : [startYear, endYear];

        const lookback = new Date(`${from}T12:00:00`);
        lookback.setDate(lookback.getDate() - 90);
        const plannedStartFrom = lookback.toISOString().slice(0, 10);

        const [assignmentRes, ...holidayResults] = await Promise.all([
          getWorkAssignments({
            plannedStartFrom,
            plannedStartTo: to,
            limit: 500,
            status: selectedStatus || undefined,
            sourceType: selectedSourceType || undefined,
            ...(isProjectEngineer && user?.id
              ? { toUserId: user.id }
              : assigneeUserId != null
                ? { toUserId: assigneeUserId }
                : {}),
          }),
          ...holidayYears.map((year) => getHolidays({ year, limit: 250 })),
        ]);

        const rows: WorkAssignment[] = (assignmentRes as any)?.data?.rows ?? [];
        const holidays = holidayResults.flatMap(
          (r: any) => r?.data?.rows ?? [],
        );
        const visibleHolidays = holidays.filter(
          (h: any) => h.date >= from && h.date <= to,
        );

        setHolidayMap(
          new Map(
            visibleHolidays.map((h: any) => [
              h.date as string,
              h.name as string,
            ]),
          ),
        );

        setEvents(
          rows.map((a) => ({
            id: String(a.id),
            start: a.plannedStartDate ?? a.created_at,
            end: a.plannedEndDate ? exclusiveEnd(a.plannedEndDate) : undefined,
            allDay: true,
            backgroundColor: "transparent",
            borderColor: "transparent",
            extendedProps: { assignment: a, color: resolveEventColor(a) },
          })),
        );
      } catch {
        setEvents([]);
      } finally {
        setCalLoading(false);
      }
    },
    [
      isProjectEngineer,
      user?.id,
      selectedStatus,
      selectedSourceType,
      assigneeUserId,
    ],
  );

  const handleDatesSet = useCallback(
    async (arg: DatesSetArg) => {
      const from = toYmd(arg.start);
      const to = toYmd(arg.end);
      currentRange.current = { from, to };
      await doFetch(from, to);
    },
    [doFetch],
  );

  // Re-fetch when filters change (FullCalendar won't re-fire datesSet on external state changes)
  const filterMounted = useRef(false);
  useEffect(() => {
    if (!filterMounted.current) {
      filterMounted.current = true;
      return;
    }
    const range = currentRange.current;
    if (!range) return;
    doFetch(range.from, range.to);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedStatus, selectedSourceType, assigneeUserId]);

  const handleEventClick = useCallback(
    (info: EventClickArg) => {
      const a: WorkAssignment = info.event.extendedProps.assignment;
      const href = sourceHref(a);
      if (href) navigate(href);
    },
    [navigate],
  );

  const hasFilters =
    selectedStatus !== "" ||
    selectedSourceType !== "" ||
    assigneeUserId != null;

  return (
    <>
      <div className="d-flex gap-2 align-items-center mb-3 flex-wrap">
        <WorkAssignmentListFilters
          selectedStatus={selectedStatus}
          onStatusChange={setSelectedStatus}
          selectedSourceType={selectedSourceType}
          onSourceTypeChange={setSelectedSourceType}
          assigneeUserId={isProjectEngineer ? null : assigneeUserId}
          onAssigneeUserIdChange={
            isProjectEngineer ? () => {} : setAssigneeUserId
          }
        />
        {hasFilters && (
          <button
            type="button"
            className="btn btn-soft-danger btn-sm d-flex align-items-center gap-1"
            onClick={() => {
              setSelectedStatus("");
              setSelectedSourceType("");
              setAssigneeUserId(null);
            }}
          >
            <i className="ri-close-circle-line" />
            Clear
          </button>
        )}
      </div>

      <div className="d-flex gap-3 align-items-center mb-3 flex-wrap">
        <span className="small text-muted">Status:</span>
        {Object.entries(workAssignmentStatusMap).map(([key, { label, color }]) => (
          <span
            key={key}
            className={`badge bg-${color}-subtle text-${color} border border-${color}-subtle`}
          >
            {label}
          </span>
        ))}
        <span className="vr" />
        <span className="d-flex align-items-center gap-1 small text-muted">
          <span
            style={{
              display: "inline-block",
              width: 12,
              height: 12,
              background: "rgba(220,53,69,0.15)",
              borderRadius: 2,
            }}
          />
          Holiday
        </span>
      </div>

      <style>{`.fc-day-holiday { background-color: rgba(220,53,69,0.07) !important; }`}</style>
      <div style={{ position: "relative" }}>
        {calLoading && (
          <div
            style={{
              position: "absolute",
              inset: 0,
              background: "rgba(var(--vz-body-bg-rgb, 255,255,255), 0.75)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 10,
              borderRadius: 4,
            }}
          >
            <div className="d-flex align-items-center gap-2 text-muted">
              <Spinner size="sm" />
              <span className="small">Loading…</span>
            </div>
          </div>
        )}
        <FullCalendar
          ref={calendarRef}
          plugins={[
            BootstrapTheme,
            dayGridPlugin,
            timeGridPlugin,
            listPlugin,
            multiMonthPlugin,
            interactionPlugin,
          ]}
          initialView="dayGridMonth"
          themeSystem="bootstrap"
          handleWindowResize={true}
          slotDuration="00:15:00"
          headerToolbar={{
            left: "prev,next today",
            center: "title",
            right: "multiMonthYear,dayGridMonth,dayGridWeek,dayGridDay",
          }}
          events={events}
          datesSet={handleDatesSet}
          eventClick={handleEventClick}
          eventContent={(info) => <EventContent info={info} />}
          dayCellClassNames={(arg) =>
            holidayMap.has(toYmd(arg.date)) ? ["fc-day-holiday"] : []
          }
          dayCellDidMount={(arg) => {
            cellEls.current.set(toYmd(arg.date), arg.el);
          }}
          dayCellWillUnmount={(arg) => {
            cellEls.current.delete(toYmd(arg.date));
          }}
          height="auto"
          dayMaxEvents={4}
          nowIndicator
        />
      </div>
    </>
  );
};

export default ScheduleCalendarView;
